<!-- eslint-disable-next-line vue/max-lines-per-block -->
<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { storeToRefs } from 'pinia'
import { useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import { array, custom, number, object, string, union, ZodIssueCode } from 'zod'

import type { DocIdentifier } from '@/types/external/2work/api/common/DocIdentifier'
import type { UpdateBRMOrganizationDocument } from '@/types/external/2work/api/request/UpdateBRMOrganizationRequest'
import type { BRMOrganizationBase } from '@/types/external/2work/doctypes/CustomDoctypes'
import type { AddressDynamic } from '@/types/external/2work/doctypes/generatorOutput/Contacts/AddressDynamic'
import type { ContactEmailDynamic } from '@/types/external/2work/doctypes/generatorOutput/Contacts/ContactEmailDynamic'
import type { ContactPhoneDynamic } from '@/types/external/2work/doctypes/generatorOutput/Contacts/ContactPhoneDynamic'
import type { TWOrganizationAIResponse } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWOrganizationAIResponse'
import type { TWWeblinksDynamic } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWWeblinksDynamic'
import type { EmployeeDynamic } from '@/types/external/2work/doctypes/generatorOutput/Setup/EmployeeDynamic'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import ButtonPrimary from '@/components/common/ui/buttons/ButtonPrimary.vue'
import ButtonSecondary from '@/components/common/ui/buttons/ButtonSecondary.vue'
import CreateOrganizationFormLong from '@/components/createRelationGroup/CreateOrganizationFormLong.vue'
import CreateOrganizationFormShort from '@/components/createRelationGroup/CreateOrganizationFormShort.vue'
import OrganizationDuplicateWarning from '@/components/createRelationGroup/OrganizationDuplicateWarning.vue'
import { useZodSchemaStore } from '@/components/editRelationGroup/sectionSelfUpdates/zodSchemaStore.ts'
import {
  useCanSuggestManagement,
  useManagementSaveNotifications,
  useManagementSuggestions,
} from '@/composables/organization/useManagementSuggestions'
import { useOrganizationDuplicateCheck } from '@/composables/relations/useOrganizationDuplicateCheck'
import { useScrollToFirstError } from '@/composables/useScrollToFirstError'
import { type DocType, DOCTYPES } from '@/constants/doctypes.ts'
import { RelationType } from '@/constants/relations'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI'
import { useAuthStore } from '@/stores/auth.ts'
import { useBackendConstantsStore } from '@/stores/backendConstantsStore'
import { useBankAccountStore } from '@/stores/bankAccountStore.ts'
import { useCreateRelationSlideInStore } from '@/stores/createRelationSlideInStore'
import { useSimpleNotificationStore } from '@/stores/simpleNotificationStore'
import { createLatestRequestGuard } from '@/utilities/latestRequest'
import { buildManagementSearchRequest } from '@/utilities/managementSuggestions'
import {
  translationFunctionBackend,
  translationFunctionCommon,
  translationFunctionDefault,
} from '@/utilities/translations'
import { validateStringAgainstEnum } from '@/utilities/typeguards'

interface CreateOrganizationFormProps {
  onCreated(response: DocIdentifier): void
}

interface OrganizationFormValues {
  customer_group: null | string
  email_ids: ContactEmailDynamic[]
  organization_name: string
  phone_nos: ContactPhoneDynamic[]
  supplier_group: null | string
  tw_addresses: AddressDynamic[]
  tw_bank: string
  tw_bank_account_bic: string
  tw_bank_account_iban: string
  tw_bank_account_owner: string
  tw_commercial_register: string
  tw_contact_origin: string
  tw_description: string
  tw_industry_type: null | string
  tw_investor_type: string
  tw_pipeline_state: string
  tw_rating: string
  tw_responsible_employee: null | string
  tw_user_tags: string[]
  tw_vat_number: string
  tw_weblinks: TWWeblinksDynamic[]
  type: string
  commission_rate?: null | number | string | undefined
  doctype?: RelationType | undefined
  tw_image?: {
    content: string
    filename: string
  }
  tw_total_loan_amount?: null | number | string | undefined
}

const props = defineProps<CreateOrganizationFormProps>()
const emit = defineEmits<{ dirtyChange: [boolean] }>()
const formRef = ref<HTMLElement | null>(null)
const scrollToFirstError = useScrollToFirstError(formRef)

const tfBackend = translationFunctionBackend()
const tfCommon = translationFunctionCommon()
const tfDefault = translationFunctionDefault()
const bankAccountStore = useBankAccountStore()
const backendConstantsStore = useBackendConstantsStore()
const { fetchPhoneNumberList, fetchWeblinkList } = backendConstantsStore
const { phoneNumberOptionList, weblinkOptionList } = storeToRefs(backendConstantsStore)

const creationLoading = ref(false)
const aiLoading = ref(false)
const simpleNotificationStore = useSimpleNotificationStore()

const aiFieldsStillVisible = ref(false)

const createRelationSlideInStore = useCreateRelationSlideInStore()
const { closeCreateRelationOverlay } = createRelationSlideInStore

const authStore = useAuthStore()

const RELATIONTYPE_TO_DOCTYPE: Partial<Record<RelationType, DocType>> = {
  [RelationType.customers]: DOCTYPES.CUSTOMER,
  [RelationType.investors]: DOCTYPES.BANK,
  [RelationType.other]: DOCTYPES.TW_OTHER,
  [RelationType.partners]: DOCTYPES.TW_PARTNER,
  [RelationType.salesPartners]: DOCTYPES.SALES_PARTNER,
  [RelationType.suppliers]: DOCTYPES.SUPPLIER,
}

const doctypeOptions = Object.values(RelationType)
  .filter((type) => {
    return ![RelationType.all, RelationType.starred].includes(type)
  })
  .filter((type) => {
    const doctype = RELATIONTYPE_TO_DOCTYPE[type]
    if (!doctype) return false

    return authStore.hasPermissions(doctype, ['create', 'read'])
  })
  .map((type) => {
    return {
      optionLabel: tfBackend(`relationType.${type}`),
      optionValue: type,
    }
  })

// Preselect the org type when the slide-in was opened for a specific one. The store
// holds the doctype string, which equals the matching RelationType enum value.
const preselectedRelationType = Object.values(RelationType).find((type) => {
  return type === createRelationSlideInStore.organisationRelationType
})

const initialValues: OrganizationFormValues = {
  commission_rate: undefined,
  customer_group: null,
  doctype: preselectedRelationType,
  email_ids: [],
  organization_name: createRelationSlideInStore.prefillMetaData?.prefillName ?? '',
  phone_nos: [],
  supplier_group: null,
  tw_addresses: [],
  tw_bank: '',
  tw_bank_account_bic: '',
  tw_bank_account_iban: '',
  tw_bank_account_owner: '',
  tw_commercial_register: '',
  tw_contact_origin: '',
  tw_description: '',
  tw_industry_type: null,
  tw_investor_type: '',
  tw_pipeline_state: '',
  tw_rating: createRelationSlideInStore.prefillMetaData?.tw_rating ?? '',
  tw_responsible_employee:
    createRelationSlideInStore.prefillMetaData?.tw_responsible_employee ?? null,
  tw_total_loan_amount: undefined,
  tw_user_tags: [],
  tw_vat_number: '',
  tw_weblinks: [],
  type: '',
}

const customAPIStore = useFrappeNestCustomAPIStore()
const aiData = ref<null | TWOrganizationAIResponse>(null)

// The management search runs next to the company search and never blocks creating the organization
const managementSuggestions = useManagementSuggestions()
// Kept in sync with the selected relation type below, so the write permission of that doctype is checked
const selectedOrganizationDoctype = ref<DocType | undefined>(undefined)
const canSuggestManagement = useCanSuggestManagement(() => selectedOrganizationDoctype.value)
const { notifySaveError, notifySaveSummary, notifySearchStillRunning } =
  useManagementSaveNotifications()

const showAllFields = ref(false)
const onShowAllFields = (): void => {
  showAllFields.value = true
}

const isPersonCustomer = ref(false)
const onIsPersonCustomerChange = (value: boolean): void => {
  isPersonCustomer.value = value
}

// Cancelling or starting again makes the answer of the running search obsolete
const aiRequests = createLatestRequestGuard()

// The management search starts after the company search and is anchored to its result (official name,
// website, register, city), so persons of a different company with the same name are not suggested
const searchManagementOf = (
  organizationName: string,
  companyData: null | TWOrganizationAIResponse,
): void => {
  // eslint-disable-next-line no-void
  void managementSuggestions.search(buildManagementSearchRequest(organizationName, companyData))
}

const triggerAI = async (organizationName: string): Promise<void> => {
  const aiRequest = aiRequests.start()
  const suggestManagement = canSuggestManagement.value && !isPersonCustomer.value
  let companyData: null | TWOrganizationAIResponse = null
  aiLoading.value = true
  if (suggestManagement) managementSuggestions.startPending(organizationName)
  try {
    const [countries] = await Promise.all([
      customAPIStore.getCountries(),
      fetchPhoneNumberList(),
      fetchWeblinkList(),
    ])
    const aiResponse = await customAPIStore.getOrganizationAIInfo(organizationName, {
      available_countries: countries.map((country) => ({
        label: tfBackend(`countryNames.${country.country_name}`),
        value: country.country_name,
      })),
      available_phone_types: phoneNumberOptionList.value.map((phoneType) => ({
        label: phoneType.optionLabel,
        value: phoneType.optionValue,
      })),
      available_weblink_types: weblinkOptionList.value.map((weblinkType) => ({
        label: weblinkType.optionLabel,
        value: weblinkType.optionValue,
      })),
    })
    if (!aiRequests.isCurrent(aiRequest)) return

    companyData = aiResponse
    if (aiResponse) {
      aiData.value = {
        ...aiResponse,
        organization_name: organizationName,
      }
    } else {
      simpleNotificationStore.triggerSimpleNotificationError(tfDefault('editRelationGroup.aiError'))
    }

    onShowAllFields()
  } catch (exception) {
    if (!aiRequests.isCurrent(aiRequest)) return
    simpleNotificationStore.triggerSimpleNotificationError(tfDefault('editRelationGroup.aiError'))
    console.error('AI-Fehler:', exception)
  } finally {
    if (aiRequests.isCurrent(aiRequest)) {
      aiLoading.value = false
      if (suggestManagement) searchManagementOf(organizationName, companyData)
    }
  }
}

// Spins until the management suggestions are loaded too, so they can still be cancelled
const aiButtonLoading = computed(
  () => aiLoading.value || managementSuggestions.searchState.value === 'loading',
)

const cancelAI = (): void => {
  aiRequests.cancel()
  aiLoading.value = false
  managementSuggestions.reset()
}

const {
  relationSchemas: {
    addressListSchema,
    emailSchema,
    organizationSchemas: { bankAccountSchema, generalDataOrganizationSchema },
    personSchemas: { metaDataPersonSchema },
    phoneNumberSchema,
    primaryAddressSchema,
    refineMents: { addressRefinement },
    weblinkSchema,
  },
} = useZodSchemaStore()

const validationSchema = object({
  doctype: custom<RelationType | undefined>(
    (val: string | undefined) => {
      if (val) {
        return validateStringAgainstEnum(RelationType, val)
      }

      return false
    },
    { message: tfDefault('editRelationGroup.requiredInput') },
  ),
})
  .required()
  .extend(metaDataPersonSchema.shape)
  .extend(weblinkSchema.shape)
  .extend(phoneNumberSchema.shape)
  .extend(emailSchema.shape)
  .extend(addressListSchema.shape)
  .extend(bankAccountSchema.shape)
  .extend(generalDataOrganizationSchema.shape)
  .extend(primaryAddressSchema.shape)
  .extend(
    object({
      commission_rate: union([number(), string()]).optional(),
      tw_image: object({
        content: string(),
        filename: string(),
      }).optional(),
      tw_investor_type: string().optional(),
      tw_total_loan_amount: union([number(), string()]).optional(),
      tw_user_tags: array(string()).optional(),
      type: string().optional(),
    }).shape,
  )

  .superRefine((values, ctx) => {
    // Only enforce bank account required fields after the user starts typing any bank input.
    const hasAnyBankInput =
      !!values.tw_bank ||
      !!values.tw_bank_account_bic ||
      !!values.tw_bank_account_iban ||
      !!values.tw_bank_account_owner

    if (!hasAnyBankInput) return

    if (!values.tw_bank_account_bic) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        fatal: true,
        message: tfDefault('editRelationGroup.requiredInput'),
        path: ['tw_bank_account_bic'],
      })
    }

    if (!values.tw_bank_account_iban) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        fatal: true,
        message: tfDefault('editRelationGroup.requiredInput'),
        path: ['tw_bank_account_iban'],
      })
    }

    if (!values.tw_bank_account_owner) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        fatal: true,
        message: tfDefault('editRelationGroup.requiredInput'),
        path: ['tw_bank_account_owner'],
      })
    }
  })
  .superRefine(addressRefinement)

const { handleSubmit, meta, resetForm, setFieldError, values } = useForm<OrganizationFormValues>({
  initialValues,
  validationSchema: toTypedSchema(validationSchema),
})

const { checkForDuplicates, duplicateMatches, resetDuplicates } = useOrganizationDuplicateCheck()

const onOrganizationNameChange = (): void => {
  checkForDuplicates(values.organization_name)
}

const ibanBackendErrorMessage = ref<string | undefined>(undefined)

watch(
  () => values.tw_bank_account_iban,
  () => {
    if (!ibanBackendErrorMessage.value) return
    ibanBackendErrorMessage.value = undefined
    setFieldError('tw_bank_account_iban', undefined)
  },
)

watch(
  () => values.doctype,
  (doctype) => {
    if (doctype !== RelationType.customers) {
      isPersonCustomer.value = false
    }
  },
)

watch(
  () => values.doctype,
  (doctype) => {
    selectedOrganizationDoctype.value = doctype ? RELATIONTYPE_TO_DOCTYPE[doctype] : undefined
  },
  { immediate: true },
)

watch(
  meta,
  (metaState: { dirty: boolean }): void => {
    emit('dirtyChange', metaState.dirty)
  },
  { immediate: true },
)

const handleAiFieldsStillVisibleChanged = (newValue: boolean): void => {
  aiFieldsStillVisible.value = newValue
}

const parseOptionalNumber = (rawValue: null | number | string | undefined): null | number => {
  if (rawValue === undefined || rawValue === null) {
    return null
  }

  const str = String(rawValue).trim()
  if (str === '') {
    return null
  }

  const num = Number(str)
  return Number.isFinite(num) ? num : null
}

const applyPostCreationUpdates = async (
  docIdentifier: DocIdentifier,
  values: OrganizationFormValues,
  response: BRMOrganizationBase,
  // eslint-disable-next-line complexity
): Promise<void> => {
  const needsImageUpdate = values.tw_image?.content && values.tw_image.filename && !response.image
  const needsEmailUpdate = values.email_ids.length > 0 && !response.email_ids?.length
  const needsPhoneUpdate = values.phone_nos.length > 0 && !response.phone_nos?.length
  const needsWeblinkUpdate = values.tw_weblinks.length > 0 && !response.tw_weblinks?.length
  const needsAddressUpdate = values.tw_addresses.length > 0 && !response.tw_addresses?.length

  if (
    needsImageUpdate ||
    needsEmailUpdate ||
    needsPhoneUpdate ||
    needsWeblinkUpdate ||
    needsAddressUpdate
  ) {
    await customAPIStore.updateOrganizationList([
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      {
        ...docIdentifier,
        ...(needsImageUpdate ? { tw_image: values.tw_image } : {}),
        ...(needsEmailUpdate ? { email_ids: values.email_ids } : {}),
        ...(needsPhoneUpdate ? { phone_nos: values.phone_nos } : {}),
        ...(needsWeblinkUpdate ? { tw_weblinks: values.tw_weblinks } : {}),
        ...(needsAddressUpdate ? { tw_addresses: values.tw_addresses } : {}),
      } as UpdateBRMOrganizationDocument,
    ])
  }
}

const saveManagementSuggestions = async (organization: DocIdentifier): Promise<void> => {
  if (canSuggestManagement.value && !isPersonCustomer.value) {
    if (managementSuggestions.searchState.value === 'loading') {
      notifySearchStillRunning()
    }
    try {
      notifySaveSummary(await managementSuggestions.save(organization))
    } catch (exception) {
      notifySaveError(exception)
    }
  }
  managementSuggestions.reset()
}

const onSubmit = handleSubmit(
  async (values: OrganizationFormValues): Promise<void> => {
    creationLoading.value = true

    const formDoctype = values.doctype
    if (!formDoctype) {
      creationLoading.value = false
      return
    }

    const payload = {
      ...values,
      commission_rate:
        formDoctype === RelationType.salesPartners
          ? (parseOptionalNumber(values.commission_rate) ?? undefined)
          : undefined,
      customer_group:
        formDoctype === RelationType.customers ? (values.customer_group ?? undefined) : undefined,
      customer_type:
        formDoctype === RelationType.customers && isPersonCustomer.value ? 'Individual' : undefined,
      doctype: formDoctype,
      supplier_group:
        formDoctype === RelationType.suppliers ? (values.supplier_group ?? undefined) : undefined,
      tw_investor_type:
        formDoctype === RelationType.investors ? values.tw_investor_type : undefined,
      tw_pipeline_state: values.tw_pipeline_state ? { name: values.tw_pipeline_state } : undefined,
      tw_total_loan_amount:
        formDoctype === RelationType.investors
          ? (parseOptionalNumber(values.tw_total_loan_amount) ?? undefined)
          : undefined,
      type: formDoctype === RelationType.other ? values.type : undefined,
      // TODO: type mismatch in BRMOrganizationBase and inside tw_addresses
      // change to: links?: (DynamicLink | DynamicLinkCreation)[] | undefined
      // change to: tw_user_tags?: string[] | undefined
      // change to: tw_addresses: AddressDynamic[] | undefined
      // tw_addresses Parameter doctype: Type 'string' is not assignable to type '"Address"'
      // tw_addresses: values.tw_addresses,
      tw_addresses: values.tw_addresses.length === 0 ? null : values.tw_addresses,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      tw_responsible_employee: (values.tw_responsible_employee ?? undefined) as
        | EmployeeDynamic
        | undefined,
    }

    try {
      const response = await customAPIStore.createOrganization(
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        payload as Partial<BRMOrganizationBase>,
      )
      const { doctype, name }: DocIdentifier = response

      await applyPostCreationUpdates({ doctype, name }, values, response)
      await saveManagementSuggestions({ doctype, name })

      resetForm()
      resetDuplicates()
      console.debug('calling onCreated for ORGANIZATION with response', response)
      props.onCreated({ doctype, name })

      simpleNotificationStore.triggerSimpleNotificationSuccess(
        tfDefault('editRelationGroup.createSuccess', {
          createObjectTranslation: tfBackend('relationGroupType.organizations'),
        }),
      )
    } catch (networkError: unknown) {
      console.error('network error:', networkError)

      bankAccountStore.onNetworkError(networkError)
      if (bankAccountStore.ibanBackendErrorMessage) {
        setFieldError('tw_bank_account_iban', bankAccountStore.ibanBackendErrorMessage)
        scrollToFirstError({
          errors: { tw_bank_account_iban: bankAccountStore.ibanBackendErrorMessage },
        })
      }

      simpleNotificationStore.triggerSimpleNotificationError(
        tfDefault('editRelationGroup.createError', {
          createObjectTranslation: tfBackend('relationGroupType.organizations'),
        }),
      )
    } finally {
      creationLoading.value = false
    }
  },
  (context): void => {
    scrollToFirstError(context)
    if (Object.keys(context.errors).length > 0) {
      // Only show a generic validation notification if errors exist outside bank fields.
      const bankAccountFields = new Set([
        'tw_bank',
        'tw_bank_account_bic',
        'tw_bank_account_iban',
        'tw_bank_account_owner',
      ])
      const hasOnlyBankAccountErrors = Object.keys(context.errors).every((key) =>
        bankAccountFields.has(key),
      )
      if (!hasOnlyBankAccountErrors) {
        simpleNotificationStore.triggerSimpleNotificationError(
          tfDefault('editRelationGroup.createErrorValidation', {
            validationObjectTranslation: tfBackend('relationGroupType.organizations'),
          }),
        )
      }
    }
    console.debug('Validation errors:', context.errors, context.values)
  },
)

const onReset = (): void => {
  resetForm()
  resetDuplicates()
  managementSuggestions.reset()
  closeCreateRelationOverlay()
}
</script>

<template>
  <div
    class="flex h-full flex-1 flex-col justify-between overflow-hidden"
    :data-testid="TEST_IDS_CREATE.createOrganizationForm.root"
  >
    <div ref="formRef" class="h-full flex-1 overflow-auto">
      <CreateOrganizationFormShort
        v-if="!showAllFields"
        :ai-loading="aiButtonLoading"
        :doctype-options="doctypeOptions"
        :is-person-customer="isPersonCustomer"
        :on-show-all-fields="onShowAllFields"
        @ai-cancel="cancelAI"
        @ai-click="triggerAI"
        @name-change="onOrganizationNameChange"
        @update:is-person-customer="onIsPersonCustomerChange"
      />
      <CreateOrganizationFormLong
        v-else
        :ai-data="aiData"
        :ai-loading="aiButtonLoading"
        :doctype-options="doctypeOptions"
        :iban-error-message="ibanBackendErrorMessage"
        :is-person-customer="isPersonCustomer"
        :management-suggestions="
          canSuggestManagement && !isPersonCustomer ? managementSuggestions : undefined
        "
        @ai-cancel="cancelAI"
        @ai-click="triggerAI"
        @ai-fields-still-visible-changed="handleAiFieldsStillVisibleChanged"
        @name-change="onOrganizationNameChange"
        @update:is-person-customer="onIsPersonCustomerChange"
      />
    </div>
  </div>

  <div v-if="!aiData || !aiFieldsStillVisible" class="flex flex-col gap-5 p-8 pt-5">
    <OrganizationDuplicateWarning v-if="duplicateMatches.length > 0" :matches="duplicateMatches" />
    <div class="flex gap-8">
      <ButtonPrimary
        class="flex-1"
        :data-testid="TEST_IDS_CREATE.createOrganizationForm.submit"
        :label="tfCommon('button.create')"
        :loading="creationLoading"
        @click="onSubmit"
      />
      <ButtonSecondary class="flex-1" :label="tfCommon('button.cancel')" @click="onReset" />
    </div>
  </div>
</template>
